export const calculateTimeDifference = (targetDateStr) => {
  // Convert the target date string to a Date object
  const targetDate = new Date(targetDateStr);

  // Get the current date and time
  const currentDate = new Date();

  // Calculate the difference in milliseconds
  const diffInMilliseconds = currentDate - targetDate;

  // Determine if the target date is in the future or the past
  const isExpired = diffInMilliseconds > 0;

  // Calculate the absolute difference in milliseconds for future or past dates
  const absoluteDiffInMilliseconds = Math.abs(diffInMilliseconds);

  // Convert milliseconds to other units
  const seconds = Math.floor(absoluteDiffInMilliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // Format the result in HH:MM format
  const formattedHours = String(hours).padStart(2, '0');  // Ensure 2-digit hours
  const formattedMinutes = String(minutes % 60).padStart(2, '0'); // Ensure 2-digit minutes

  const timeDifference = `${formattedHours}:${formattedMinutes} hours`;

  // Return the relevant information
  return {
    isExpired,         // Boolean indicating if the target date is in the past
    hours,             // Difference in hours
    days,              // Difference in days (if more than 24 hours)
    timeDifference     // Readable time difference (HH:MM or days)
  };
};

export const getDateOnly = (dateStr) => {
  // Convert the date string to a Date object
  const date = new Date(dateStr);

  // Extract the year, month, and day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed, so add 1
  const day = String(date.getDate()).padStart(2, '0'); // Ensure 2-digit day

  // Return the formatted date as YYYY-MM-DD
  return `${year}-${month}-${day}`;
};

export const getDay = (dateInput) => {
  const today = new Date();
  const givenDate = new Date(dateInput);

  // Set the time portion of today to be 00:00:00 to only compare the date part
  today.setHours(0, 0, 0, 0);
  givenDate.setHours(0, 0, 0, 0);

  // Check if the given date is today
  if (givenDate.getTime() === today.getTime()) {
    return 'Today';
  }

  // Check if the given date is yesterday
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1); // Subtract 1 day to get yesterday
  if (givenDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  // If it's neither today nor yesterday, return the date in 'YYYY-MM-DD' format
  return dateInput;
};

export const getDayFromDate = (dateString) => {
  const date = new Date(dateString);
  return date.getDate(); // Returns the day of the month (e.g., 13)
};

export const getMonthYearFromDate = (dateString) => {
  const date = new Date(dateString);
  const options = { year: 'numeric', month: 'short' }; // "short" gives the abbreviated month (e.g., "Jan")
  return date.toLocaleDateString('en-US', options); // e.g., "Jan 2026"
};

export const compareTimes = (startTime, endTime) => {
  const [startHours, startMinutes] = startTime.split(":");
  const [endHours, endMinutes] = endTime.split(":");

  // Create Date objects for comparison (use current date for simplicity)
  const startDate = new Date();
  startDate.setHours(startHours, startMinutes, 0, 0); // Set time

  const endDate = new Date();
  endDate.setHours(endHours, endMinutes, 0, 0); // Set time

  // Compare if startDate is earlier than endDate
  return startDate < endDate;
};

export const compareDates = (startDateStr, endDateStr) => {
  // Convert the date strings to Date objects
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);

  // Compare if startDate is earlier than endDate
  return startDate <= endDate;
};

export const getTimeHHMM = (time) => {
  if (!(time instanceof Date)) {
    return null; // Handle invalid input (not a Date object)
  }

  const hours = String(time.getHours()).padStart(2, '0');
  const minutes = String(time.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const calculateTimeDifferenceHHMM = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return null; // Handle invalid input
  }

  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  if (
    isNaN(startHours) ||
    isNaN(startMinutes) ||
    isNaN(endHours) ||
    isNaN(endMinutes) ||
    startHours < 0 ||
    startHours > 23 ||
    startMinutes < 0 ||
    startMinutes > 59 ||
    endHours < 0 ||
    endHours > 23 ||
    endMinutes < 0 ||
    endMinutes > 59
  ) {
    return null; // Handle invalid time format
  }

  let diffHours = endHours - startHours;
  let diffMinutes = endMinutes - startMinutes;

  if (diffMinutes < 0) {
    diffHours--;
    diffMinutes += 60;
  }

  if (diffHours < 0) {
    return "00:00"; // Return "00:00" for negative difference
  }

  const formattedHours = String(diffHours).padStart(2, '0');
  const formattedMinutes = String(diffMinutes).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}`;
};